/**
 * OperatorOnlyRoute.jsx — RC 1.62 Phase D completion (D-3).
 *
 * Route-level back-stop for operator-only surfaces. Mirrors the
 * shape of `ProtectedRoute` (children-as-element) but adds an
 * additional `is_operator` gate on top of authentication, resolved
 * via the server-projected `/api/auth/me/access::is_operator` flag.
 *
 * Hard rules:
 *   • Anonymous → handled by `ProtectedRoute` upstream (this guard
 *     assumes the user is already authenticated).
 *   • Authenticated non-operator → silent <Navigate replace to="/dashboard"/>.
 *     No flash of forbidden content; no toast (the surface simply
 *     "doesn't exist" for them).
 *   • Authenticated operator → render children verbatim.
 *
 * Source-of-truth: `useIsOperator()` (server-projected boolean).
 * The frontend NEVER carries a hardcoded operator UUID or email.
 * DO NOT fork the predicate here.
 */
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useIsOperator } from '@/lib/useIsOperator';

export default function OperatorOnlyRoute({ children, redirectTo = '/dashboard' }) {
  const { loading: authLoading } = useAuth();
  const { isOperator, loading: opLoading } = useIsOperator();
  // While EITHER auth or the operator projection is hydrating,
  // render nothing — the parent `ProtectedRoute` already handles
  // its own loading skeleton, so returning `null` here just defers
  // the operator check by one tick and avoids a pre-hydration
  // redirect storm.
  if (authLoading || opLoading) return null;
  if (!isOperator) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}
